import type { z } from "zod/v4";
import {
	clubTournamentBracketSchema,
	clubTournamentDetailResponseSchema,
	clubTournamentListResponseSchema,
	clubTournamentSchema,
	clubTournamentTeamSchema,
	type createClubTournamentSchema,
	type markClubTournamentWalkoverSchema,
	type registerClubTournamentTeamSchema,
	type seedClubTournamentSchema,
	type setClubTournamentMemberLicenseSchema,
	type setClubTournamentVisibilitySchema,
	type updateClubTournamentSchema,
} from "../schemas/club-tournament";
import { fetchApi, fetchVoid } from "./internal";

export const tournaments = {
	list: (params?: {
		q?: string;
		limit?: number;
		offset?: number;
		club_id?: string;
		status?: string;
		visibility?: string[];
		from?: string;
		to?: string;
	}) => {
		const searchParams = new URLSearchParams();
		if (params?.q) searchParams.set("q", params.q);
		if (params?.limit) searchParams.set("limit", String(params.limit));
		if (params?.offset) searchParams.set("offset", String(params.offset));
		if (params?.club_id) searchParams.set("club_id", params.club_id);
		if (params?.status) searchParams.set("status", params.status);
		if (params?.visibility) {
			for (const v of params.visibility) {
				searchParams.append("visibility", v);
			}
		}
		if (params?.from) searchParams.set("from", params.from);
		if (params?.to) searchParams.set("to", params.to);
		const qs = searchParams.toString();
		return fetchApi(`/tournaments${qs ? `?${qs}` : ""}`, clubTournamentListResponseSchema);
	},
	get: (id: string) => fetchApi(`/tournaments/${id}`, clubTournamentDetailResponseSchema),
	create: (data: z.infer<typeof createClubTournamentSchema>) =>
		fetchApi(`/tournaments`, clubTournamentSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	update: (id: string, data: z.infer<typeof updateClubTournamentSchema>) =>
		fetchApi(`/tournaments/${id}`, clubTournamentSchema, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	setVisibility: (id: string, data: z.infer<typeof setClubTournamentVisibilitySchema>) =>
		fetchApi(`/tournaments/${id}/visibility`, clubTournamentSchema, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	openRegistration: (id: string) =>
		fetchVoid(`/tournaments/${id}/registration/open`, { method: "POST" }),
	closeRegistration: (id: string) =>
		fetchVoid(`/tournaments/${id}/registration/close`, { method: "POST" }),
	registerTeam: (id: string, data: z.infer<typeof registerClubTournamentTeamSchema>) =>
		fetchApi(`/tournaments/${id}/teams`, clubTournamentTeamSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	withdrawTeam: (id: string, teamId: string) =>
		fetchVoid(`/tournaments/${id}/teams/${teamId}`, { method: "DELETE" }),
	checkIn: (id: string, teamId: string) =>
		fetchVoid(`/tournaments/${id}/teams/${teamId}/check-in`, {
			method: "POST",
		}),
	setMemberLicense: (
		id: string,
		teamId: string,
		memberId: string,
		data: z.infer<typeof setClubTournamentMemberLicenseSchema>,
	) =>
		fetchVoid(`/tournaments/${id}/teams/${teamId}/members/${memberId}/license`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	removeMemberLicense: (id: string, teamId: string, memberId: string) =>
		fetchVoid(`/tournaments/${id}/teams/${teamId}/members/${memberId}/license`, {
			method: "DELETE",
		}),
	seed: (id: string, data: z.infer<typeof seedClubTournamentSchema>) =>
		fetchVoid(`/tournaments/${id}/seed`, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	generateBracket: (id: string) =>
		fetchApi(`/tournaments/${id}/bracket`, clubTournamentBracketSchema, {
			method: "POST",
		}),
	start: (id: string) => fetchVoid(`/tournaments/${id}/start`, { method: "POST" }),
	markWalkover: (
		id: string,
		nodeId: string,
		data: z.infer<typeof markClubTournamentWalkoverSchema>,
	) =>
		fetchVoid(`/tournaments/${id}/nodes/${nodeId}/walkover`, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	complete: (id: string) => fetchVoid(`/tournaments/${id}/complete`, { method: "POST" }),
	cancel: (id: string) => fetchVoid(`/tournaments/${id}/cancel`, { method: "POST" }),
};
